// Login.jsx
import React, { useEffect, useState } from "react";
import CustomInput from "../components/CustomInput";
import { useLocation, useNavigate } from "react-router-dom";
import * as yup from "yup";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import {
  forgotPassword,
  login,
  resetAuthState,
} from "../features/auth/authSlice";

import { FaEye, FaEyeSlash } from "react-icons/fa";
import "../styles/Login.css";

let loginSchema = yup.object().shape({
  email: yup
    .string()
    .email("Email should be valid")
    .required("Email is Required"),
  password: yup.string().required("Password is Required"),
});

let forgotSchema = yup.object().shape({
  email: yup
    .string()
    .email("Email should be valid")
    .required("Email is Required"),
});

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [sessionMessage, setSessionMessage] = useState("");
  const [resetSuccessMessage, setResetSuccessMessage] = useState("");
  const [showForgotCard, setShowForgotCard] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const authState = useSelector((state) => state.auth);

  const {
    user,
    isSuccess,
    isLoading,
    message,
    forgotPasswordSuccess,
  } = authState;

  const loginFormik = useFormik({
    initialValues: {
      email: location.state?.email || "",
      password: "",
    },
    enableReinitialize: true,
    validationSchema: loginSchema,
    onSubmit: (values) => {
      dispatch(login(values));
    },
  });

  const forgotFormik = useFormik({
    initialValues: {
      email: "",
    },
    validationSchema: forgotSchema,
    onSubmit: (values) => {
      dispatch(forgotPassword(values));
    },
  });

  useEffect(() => {
    const expiredMessage = localStorage.getItem("sessionExpiredMessage");

    if (expiredMessage) {
      setSessionMessage(expiredMessage);
      localStorage.removeItem("sessionExpiredMessage");
    }
  }, []);

  useEffect(() => {
    if (location.state?.successMessage) {
      setResetSuccessMessage(location.state.successMessage);
    }
  }, [location.state]);

  useEffect(() => {
    if (isSuccess && user) {
      navigate("/admin/dashboard", { replace: true });
    }
  }, [user, isSuccess, navigate]);

  useEffect(() => {
    if (forgotPasswordSuccess) {
      forgotFormik.resetForm();
    }
  }, [forgotPasswordSuccess]);

  useEffect(() => {
    return () => {
      dispatch(resetAuthState());
    };
  }, [dispatch]);

  return (
    <div className="login-front">
      <div className="login-overlay" />

      <div className="login-wrapper">
        <div className="login-card">
          {!showForgotCard ? (
            <>
              <div className="login-brand">
                <h2 className="login-title">Welcome Back</h2>

                <p className="login-subtitle">
                  Sign in to access your Admin dashboard.
                </p>
              </div>

              {sessionMessage ? (
                <div className="alert alert-warning text-center py-2 mt-3">
                  {sessionMessage}
                </div>
              ) : null}

              {resetSuccessMessage ? (
                <div className="alert alert-success text-center py-2 mt-3">
                  {resetSuccessMessage}
                </div>
              ) : null}

              {message && !forgotPasswordSuccess ? (
                <div className="login-error-box mt-3 text-center">
                  {message?.message === "Rejected"
                    ? "You are not authorised to access this account."
                    : message?.message || message}
                </div>
              ) : null}

              <form onSubmit={loginFormik.handleSubmit} className="mt-4">
                <div className="mb-3">
                  <CustomInput
                    type="text"
                    label="Email Address"
                    id="email"
                    name="email"
                    onChng={loginFormik.handleChange("email")}
                    onBlr={loginFormik.handleBlur("email")}
                    val={loginFormik.values.email}
                  />

                  <div className="error mt-2">
                    {loginFormik.touched.email &&
                      loginFormik.errors.email}
                  </div>
                </div>

                {/* PASSWORD FIELD */}
                <div className="mb-2">
                  <div className="password-wrapper">
                    <CustomInput
                      type={showPassword ? "text" : "password"}
                      label="Password"
                      id="pass"
                      name="password"
                      onChng={loginFormik.handleChange("password")}
                      onBlr={loginFormik.handleBlur("password")}
                      val={loginFormik.values.password}
                    />

                    <span
                      className="password-toggle"
                      onClick={() =>
                        setShowPassword(!showPassword)
                      }
                    >
                      {showPassword ? (
                        <FaEyeSlash className="show-icon" />
                      ) : (
                        <FaEye className="show-icon" />
                      )}
                    </span>
                  </div>

                  <div className="error mt-2">
                    {loginFormik.touched.password &&
                      loginFormik.errors.password}
                  </div>
                </div>

                <div className="d-flex justify-content-end mb-4">
                  <button
                    type="button"
                    className="forgot-password-link"
                    onClick={() => {
                      dispatch(resetAuthState());
                      setShowForgotCard(true);
                    }}
                  >
                    Forgot Password?
                  </button>
                </div>

                <button
                  className="login-btn"
                  type="submit"
                  disabled={isLoading}
                >
                  {isLoading ? "Logging in..." : "Sign In"}
                </button>
              </form>
            </>
          ) : (
            <>
              <div className="login-brand">
                <h2 className="login-title">
                  Forgot Password
                </h2>

                <p className="login-subtitle">
                  Enter your email address and we will send
                  you a secure password reset link.
                </p>
              </div>

              {message ? (
                <div
                  className={`mt-3 text-center py-2 rounded-3 ${
                    forgotPasswordSuccess
                      ? "alert alert-success"
                      : "login-error-box"
                  }`}
                >
                  {message?.message || message}
                </div>
              ) : null}

              <form
                onSubmit={forgotFormik.handleSubmit}
                className="mt-4"
              >
                <div className="mb-3">
                  <CustomInput
                    type="text"
                    label="Email Address"
                    id="forgot-email"
                    name="email"
                    onChng={forgotFormik.handleChange("email")}
                    onBlr={forgotFormik.handleBlur("email")}
                    val={forgotFormik.values.email}
                  />

                  <div className="error mt-2">
                    {forgotFormik.touched.email &&
                      forgotFormik.errors.email}
                  </div>
                </div>

                <button
                  className="login-btn"
                  type="submit"
                  disabled={isLoading}
                >
                  {isLoading
                    ? "Sending reset link..."
                    : "Send Reset Link"}
                </button>

                <div className="text-center mt-3">
                  <button
                    type="button"
                    className="forgot-password-link"
                    onClick={() => {
                      dispatch(resetAuthState());
                      setShowForgotCard(false);
                    }}
                  >
                    Back to Sign In
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;